<template>
  <div data-test="loginForm">
    <v-card
      v-if="errors.length > 0"
      color="alert"
      role="alert"
      class="pa-2 mb-4"
    >
      <ul>
        <li v-for="(err, i) in errors" :key="i">{{ err }}</li>
      </ul>
    </v-card>

    <form class="mobile-max-width" @keyup.enter.prevent="onSubmit">
      <template v-if="openId">
        <SignInOpenId />

        <div class="text-center">
          <small> - {{ $t("navigation:loginModal.dividerText") }} - </small>
        </div>
      </template>

      <IdentityHints
        v-if="identityHints.length"
        v-model="form.identityHint"
        :identity-hints="identityHints"
        :loading="loading"
      />

      <v-text-field
        v-model="form.anyIdentity"
        :label="
          $t(
            'signin:signinform.LicenceNumberOrEmail.label',
            'Licence number or email',
            'Label of the licence number or email field in signin form'
          )
        "
        outlined
        autofocus
        :disabled="loading"
        data-test="username"
        autocomplete="username"
        hide-details
        required
        class="my-2 mobile-max-width"
      />
      <v-text-field
        v-model="form.password"
        outlined
        hide-details
        :disabled="loading"
        :label="
          $t(
            'signin:signinform.Password.label',
            'Password',
            'Label of the password field in signin form'
          )
        "
        data-test="password"
        :type="showPassword ? undefined : 'password'"
        autocomplete="current-password"
        class="my-2"
      >
        <template #append>
          <v-btn
            icon
            style="margin-top: -6px"
            @click="() => (showPassword = !showPassword)"
            ><v-icon
              small
              v-text="!showPassword ? '$iconInputHiddenOn' : '$iconInputHiddenOff'"
          /></v-btn>
        </template>
      </v-text-field>

      <div class="mx-4 mb-2">
        <a
          href="/request_password_reset"
          style="text-decoration: underline !important"
          @click.prevent="resetPassword"
        >
          {{
            $t(
              "navigation:link.forgotPassword",
              "Forgot password?",
              "Link to start pasword recovery flow"
            )
          }}
        </a>
      </div>

      <v-row>
        <v-col cols="12">
          <v-btn
            data-test="submitLogin"
            color="accent"
            rounded
            block
            :disabled="loading"
            :loading="loading"
            @click="onSubmit"
          >
            {{
              $t(
                "navigation:form.submit.login",
                "Login",
                "Login button in login form"
              )
            }}
          </v-btn>
        </v-col>

        <v-col cols="12">
          <v-btn
            outlined
            rounded
            block
            :disabled="loading"
            data-test="joinFederation"
            depressed
            @click="joinFipa"
          >
            {{
              $t(
                "navigation:button.requestPasswordReset",
                "Join KIPA",
                "Button to request password for login in"
              )
            }}
          </v-btn>
        </v-col>
      </v-row>
    </form>
  </div>
</template>

<script>
import { defineComponent } from "vue";
import gql from "fraql";
import { get } from "lodash-es";
import SignInOpenId from "@/components/SignInOpenId.vue";
import IdentityHints from "@elements/IdentityHints.vue";

export default defineComponent({
  name: "SignInForm",
  components: {
    SignInOpenId,
    IdentityHints
  },
  emits: [
    "closeBox" // For AppNavigation to tell its dialog to be closed
  ],
  data() {
    return {
      form: {
        identityHint: null,
        anyIdentity: null,
        password: null
      },
      showPassword: false,
      loading: false,
      errors: [],
      openId: Boolean(process.env.VUE_APP_OPENID_PROVIDER),
      identityHints: []
    };
  },
  methods: {
    joinFipa() {
      if (this.$auth.checkUserCanPerformMutationsInScopeAny("personSignup")) {
        this.$router.push({ name: "PersonSelfSignup", hash: "" });
      } else if (this.$route.name != "JoinFederation") {
        this.$router.push({ name: "JoinFederation" });
      }
      this.$emit("closeBox");
    },
    resetPassword() {
      if (this.$route.name != "PersonRequestPasswordReset") {
        this.$router.push({ name: "PersonRequestPasswordReset" });
      }
      this.$emit("closeBox");
    },
    async onSubmit() {
      this.loading = true;
      this.errors = [];

      if (!this.form.anyIdentity) {
        this.errors.push(
          this.$t(
            "navigation:loginModal.validationError.usernameCannotBeEmpty",
            "Licence number or email input is empty."
          )
        );
      }

      if (!this.form.password) {
        this.errors.push(
          this.$t(
            "navigation:loginModal.passwordCannotBeEmpty",
            "Password field is empty"
          )
        );
      }

      if (this.errors.length > 0) {
        this.loading = false;
        return;
      }

      try {
        const { data } = await this.$apollo.mutate({
          mutation: gql`
            mutation personSignIn(
              $anyIdentity: String!
              $password: String!
              $identityHint: String
            ) {
              personSignIn(
                input: {
                  anyIdentity: {
                    anyIdentity: $anyIdentity
                    password: $password
                    identityHint: $identityHint
                  }
                }
              ) {
                signInPayload {
                  token
                  person {
                    id
                  }
                }
                ${IdentityHints.fragments.identityHintsSignIn}
                status
              }
            }
          `,
          variables: this.form
        });

        // Result
        if (data.personSignIn.identityHints?.length) {
          this.identityHints = data.personSignIn.identityHints;
        }

        const userId = get(data, "personSignIn.signInPayload.person.id");
        const token = get(data, "personSignIn.signInPayload.token");

        if (userId && token) {
          this.$auth.setSession(token);

          if (sessionStorage.getItem("redirection")) {
            const to = JSON.parse(sessionStorage.getItem("redirection"));
            sessionStorage.removeItem("redirection");
            this.$router.replace({
              name: to.name,
              params: to?.params ?? undefined
            });
            location.reload();
          } else {
            location.href = "/";
          }
          return;
        }

        this.loading = false;

        switch (data.personSignIn.status) {
          case "pick_hint":
            this.errors.push(
              this.$t(
                "signin:signinform.identityHint.alert",
                "More than one account found, please choose the account based on hint below"
              )
            );
            break;
          case "not_allowed":
            this.errors.push(
              this.$t(
                "signin:signinform.AccountValidationError.Validation",
                "Cannot sign in as your account is not active. Please make sure your club membership is active or your user roles have expired.",
                "Account validation error during sign-in: account is not valid or all roles expired"
              )
            );
            break;
          default:
            this.errors.push(
              this.$t(
                "signin:signinform.UserNameOrPasswordValidationError.Validation",
                "Cannot log in. Please note that your old KIPA username and password do not work in new KIPA. Please use Join KIPA to get a new password.",
                "Username or password validation error during sign-in: account is not valid or all roles expired"
              )
            );
        }
      } catch (error) {
        this.errors.push(
          this.$t(
            "common:tehnicalError",
            "Unfortunately, system encountered a problem. Our technical team is investigating, please try again later."
          )
        );
        this.$sentryThrowError.throwError(error, { level: "error" });
      } finally {
        this.loading = false;
      }
    }
  }
});
</script>
